import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Search, Image, Video, Code, Link, LayoutDashboard, Upload } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ContentCard } from './ContentCard';
import { ContentUploadDialog } from './ContentUploadDialog';

interface ContentItem {
  id: string;
  name: string;
  description: string | null;
  content_type: 'image' | 'video' | 'html' | 'url' | 'dashboard';
  storage_path: string | null;
  external_url: string | null;
  html_content: string | null;
  dashboard_type: string | null;
  duration_seconds: number;
  thumbnail_url: string | null;
  is_active: boolean;
  created_at: string;
}

interface ContentLibraryProps {
  businessId: string;
}

export function ContentLibrary({ businessId }: ContentLibraryProps) {
  const [content, setContent] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  useEffect(() => {
    fetchContent();
  }, [businessId]);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('signage_content_items')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContent(data || []);
    } catch (err) {
      console.error('Failed to fetch content:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contentId: string) => {
    const item = content.find(c => c.id === contentId);

    try {
      // Delete from storage if applicable
      if (item?.storage_path) {
        await supabase.storage
          .from('signage-content')
          .remove([item.storage_path]);
      }

      // Delete from database
      const { error } = await supabase
        .from('signage_content_items')
        .delete()
        .eq('id', contentId);

      if (error) throw error;

      toast.success('Content deleted');
      fetchContent();
    } catch (err) {
      console.error('Failed to delete content:', err);
      toast.error('Failed to delete content');
    }
  };

  const filteredContent = content.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || item.content_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <Image className="w-4 h-4" />;
      case 'video':
        return <Video className="w-4 h-4" />;
      case 'html':
        return <Code className="w-4 h-4" />;
      case 'url':
        return <Link className="w-4 h-4" />;
      case 'dashboard':
        return <LayoutDashboard className="w-4 h-4" />;
      default:
        return <Image className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-10 w-32 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="aspect-video bg-muted" />
              <CardContent className="p-3">
                <div className="h-4 w-24 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Content Library</h2>
          <p className="text-sm text-muted-foreground">
            {content.length} item{content.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={() => setIsUploadOpen(true)}>
          <Upload className="w-4 h-4 mr-2" />
          Upload Content
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="image">
              <span className="flex items-center gap-2">
                <Image className="w-4 h-4" /> Images
              </span>
            </SelectItem>
            <SelectItem value="video">
              <span className="flex items-center gap-2">
                <Video className="w-4 h-4" /> Videos
              </span>
            </SelectItem>
            <SelectItem value="html">
              <span className="flex items-center gap-2">
                <Code className="w-4 h-4" /> HTML
              </span>
            </SelectItem>
            <SelectItem value="url">
              <span className="flex items-center gap-2">
                <Link className="w-4 h-4" /> URLs
              </span>
            </SelectItem>
            <SelectItem value="dashboard">
              <span className="flex items-center gap-2">
                <LayoutDashboard className="w-4 h-4" /> Dashboards
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content Grid */}
      {filteredContent.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Image className="w-8 h-8 text-muted-foreground" />
            </div>
            <CardTitle className="text-lg mb-2">
              {searchQuery || typeFilter !== 'all' ? 'No content found' : 'No content yet'}
            </CardTitle>
            <CardDescription className="text-center max-w-sm mb-4">
              {searchQuery || typeFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Upload images, videos, or create custom content to display on your screens'}
            </CardDescription>
            {!searchQuery && typeFilter === 'all' && (
              <Button onClick={() => setIsUploadOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Your First Content
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredContent.map((item) => (
            <ContentCard
              key={item.id}
              content={item}
              onDelete={() => handleDelete(item.id)}
              onRefresh={fetchContent}
            />
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <ContentUploadDialog
        open={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        businessId={businessId}
        onUploaded={fetchContent}
      />
    </div>
  );
}
